import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { cn } from "../lib/utils";
import { LIEN_BOUTON } from "./coloriage-commun";
import { ALERTE_GRISE } from "./coloriage-demandes";
import { messageDEchec, useCreerCahier } from "./coloriage-requetes";
import {
  formatLong,
  LIBELLE_NIVEAU,
  NIVEAU_PAR_DEFAUT,
  NIVEAUX,
  type Niveau,
  refusDeCleCahier,
} from "./coloriage-textes";
import {
  type CahierResume,
  cleDeTitre,
  type NouveauCahier,
  rogner,
  TITRE_CAHIER_MAX,
} from "./contract";

/**
 * « Nouveau cahier » (#271, spec du 03/10/2026, section 3) : le panneau que le bouton de
 * l'accueil ouvre, sur le modele de « Nouveau livre » des Histoires (`histoires-accueil.tsx`).
 *
 * Le titre, la cle proposee depuis lui tant qu'on ne la touche pas, le theme, l'age, le nombre
 * de dessins et le format : le brief. Le sommaire s'ecrit ensuite sur la page du cahier.
 * `preparation` vaut `nicolas` : « Claude en session » et « Claude tout seul » arrivent avec
 * le MCP et l'agent qui ecrit (#272, #273), et l'ecran n'offre pas encore de choix.
 *
 * Pas de maquette a ce ticket : la disposition reprend celle de « Nouveau livre ».
 */

/** Les formats d'un cahier, celui de KDP d'abord : c'est le format par defaut. */
const FORMATS: readonly CahierResume["format"][] = ["kdp", "a4"];

/** Le nombre de dessins voulu, comme le brief du contrat : de 1 a 60, 24 par defaut. */
export const DESSINS_MIN = 1;
export const DESSINS_MAX = 60;
export const DESSINS_PAR_DEFAUT = 24;

/** Le nombre de dessins tape, lu en entier ; null s'il sort de 1 a 60 ou n'est pas un entier. */
export function dessinsLus(saisie: string): number | null {
  const propre = saisie.trim();
  if (!/^\d{1,3}$/.test(propre)) return null;
  const nombre = Number(propre);
  return nombre >= DESSINS_MIN && nombre <= DESSINS_MAX ? nombre : null;
}

/**
 * La cle proposee depuis le titre : `cleDeTitre`, comme le PC la calculerait. Un titre vide ne
 * propose rien (l'ardoise d'un texte vide vaut `page`, une cle sans rapport avec le cahier).
 */
export function cleProposee(titre: string): string {
  return rogner(titre) === "" ? "" : cleDeTitre(titre);
}

/** Les champs du module ; 44 px au doigt, comme les boutons (`max-md:min-h-11`). */
const CHAMP =
  "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 max-md:min-h-11";
const ETIQUETTE = "text-sm font-semibold";
const AIDE = "text-xs text-muted-foreground";
const REFUS = "text-sm text-destructive";

/**
 * Le panneau. Ouvert, le titre prend le focus ; « Annuler » rend la main a l'accueil, qui
 * rend le focus au bouton. Cree, le brouillon s'ouvre sur sa page.
 */
export function NouveauCahierPanneau({ fermer }: { fermer: () => void }) {
  const navigate = useNavigate();
  const creer = useCreerCahier();
  const [titre, setTitre] = useState("");
  const [cleSaisie, setCleSaisie] = useState<string | null>(null);
  const [theme, setTheme] = useState("");
  const [niveau, setNiveau] = useState<Niveau>(NIVEAU_PAR_DEFAUT);
  const [dessins, setDessins] = useState(String(DESSINS_PAR_DEFAUT));
  const [format, setFormat] = useState<CahierResume["format"]>("kdp");
  const [tente, setTente] = useState(false);
  // La cle que la console a refusee (409 `cle_prise`) : le refus vaut tant qu'elle n'a pas change.
  const [clePrise, setClePrise] = useState<{ cle: string; message: string } | null>(null);
  // Un verrou synchrone : un double clic arrive avant que `isPending` ne soit rendu.
  const envoi = useRef(false);
  const champTitre = useRef<HTMLInputElement>(null);
  const champCle = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    champTitre.current?.focus();
  }, []);

  const cle = cleSaisie ?? cleProposee(titre);
  const nombre = dessinsLus(dessins);
  // Rogne comme le contrat (`rogner`, le `str.strip` de Python) : ce que le PC verra.
  const refusTitre = rogner(titre) === "" ? "Le titre est vide." : null;
  const refusCle =
    refusDeCleCahier(cle) ?? (clePrise !== null && clePrise.cle === cle ? clePrise.message : null);
  const refusTheme =
    theme.trim() === "" ? "Le thème est vide : dis en une phrase ce que le cahier montre." : null;
  const refusDessins =
    nombre === null ? `Un nombre entier de ${DESSINS_MIN} à ${DESSINS_MAX} dessins.` : null;

  // Une cle touchee se juge tout de suite ; le reste, au premier envoi.
  const montrerTitre = tente && refusTitre !== null;
  const montrerCle = (tente || cleSaisie !== null || clePrise !== null) && refusCle !== null;
  const montrerTheme = tente && refusTheme !== null;
  const montrerDessins = tente && refusDessins !== null;

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setTente(true);
    if (envoi.current || nombre === null) return;
    if (refusTitre !== null || refusCle !== null || refusTheme !== null) return;
    // Chaque champ vient d'etre juge par les regles du contrat (`nouveauCahierSchema`) : les
    // longueurs par `maxLength`, la cle par `cleNormale`, le nombre de 1 a 60.
    const corps: NouveauCahier = {
      titre: rogner(titre),
      cle,
      format,
      brief: { theme: theme.trim(), niveau, dessins: nombre, consigne: null },
      preparation: "nicolas",
    };
    envoi.current = true;
    creer.mutate(corps, {
      onSuccess: (reponse) => {
        void navigate({
          to: "/coloriage/cahiers/$id",
          params: { id: String(reponse.cahier.id) },
        });
      },
      onError: (error) => {
        if (error instanceof ApiError && error.code === "cle_prise") {
          setClePrise({ cle, message: error.message });
          champCle.current?.focus();
        }
      },
      onSettled: () => {
        envoi.current = false;
      },
    });
  };

  const autreRefus =
    creer.isError && !(creer.error instanceof ApiError && creer.error.code === "cle_prise");

  return (
    <section
      aria-labelledby={`${id}-titre-panneau`}
      className="max-w-3xl rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="mb-4 text-base font-semibold" id={`${id}-titre-panneau`}>
        Nouveau cahier
      </h2>
      <form className="flex flex-col gap-4" noValidate onSubmit={envoyer}>
        <div className="grid items-start gap-4 md:grid-cols-2">
          <Champ
            aide={`${TITRE_CAHIER_MAX} caractères au plus, pour tenir sur la page de titre.`}
            etiquette="Titre"
            id={`${id}-titre`}
            refus={montrerTitre ? refusTitre : null}
          >
            <input
              aria-describedby={
                montrerTitre ? `${id}-titre-aide ${id}-titre-refus` : `${id}-titre-aide`
              }
              aria-invalid={montrerTitre || undefined}
              className={CHAMP}
              id={`${id}-titre`}
              maxLength={TITRE_CAHIER_MAX}
              onChange={(evenement) => setTitre(evenement.target.value)}
              placeholder="Les animaux de la ferme"
              ref={champTitre}
              value={titre}
            />
          </Champ>
          <Champ
            aide="Minuscules, chiffres et tirets : le nom du dossier du cahier sur le PC. Elle ne change plus ensuite."
            etiquette="Clé"
            id={`${id}-cle`}
            refus={montrerCle ? refusCle : null}
          >
            <input
              aria-describedby={montrerCle ? `${id}-cle-aide ${id}-cle-refus` : `${id}-cle-aide`}
              aria-invalid={montrerCle || undefined}
              autoCapitalize="none"
              autoCorrect="off"
              className={cn(CHAMP, "font-mono")}
              id={`${id}-cle`}
              maxLength={120}
              onChange={(evenement) => setCleSaisie(evenement.target.value)}
              placeholder="se remplit depuis le titre"
              ref={champCle}
              spellCheck={false}
              value={cle}
            />
          </Champ>
        </div>
        <Champ
          aide="En une phrase ou deux : ce que les dessins montrent. Le sommaire en tirera une page par dessin."
          etiquette="Thème"
          id={`${id}-theme`}
          refus={montrerTheme ? refusTheme : null}
        >
          <textarea
            aria-describedby={
              montrerTheme ? `${id}-theme-aide ${id}-theme-refus` : `${id}-theme-aide`
            }
            aria-invalid={montrerTheme || undefined}
            className={CHAMP}
            id={`${id}-theme`}
            maxLength={500}
            onChange={(evenement) => setTheme(evenement.target.value)}
            placeholder="Les animaux de la ferme, chacun dans son coin de la ferme"
            rows={2}
            value={theme}
          />
        </Champ>
        <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,12rem)]">
          <Choix
            etiquette="Âge"
            nom={`${id}-niveau`}
            options={NIVEAUX.map((valeur) => ({ valeur, libelle: LIBELLE_NIVEAU[valeur] }))}
            poser={setNiveau}
            valeur={niveau}
          />
          <Champ
            aide={`De ${DESSINS_MIN} à ${DESSINS_MAX}.`}
            etiquette="Nombre de dessins"
            id={`${id}-dessins`}
            refus={montrerDessins ? refusDessins : null}
          >
            <input
              aria-describedby={
                montrerDessins ? `${id}-dessins-aide ${id}-dessins-refus` : `${id}-dessins-aide`
              }
              aria-invalid={montrerDessins || undefined}
              className={cn(CHAMP, "tabular-nums")}
              id={`${id}-dessins`}
              inputMode="numeric"
              max={DESSINS_MAX}
              min={DESSINS_MIN}
              onChange={(evenement) => setDessins(evenement.target.value)}
              step={1}
              type="number"
              value={dessins}
            />
          </Champ>
        </div>
        <Choix
          etiquette="Format"
          nom={`${id}-format`}
          options={FORMATS.map((valeur) => ({ valeur, libelle: formatLong(valeur) }))}
          poser={setFormat}
          valeur={format}
        />
        <p className={ALERTE_GRISE}>
          <strong className="font-semibold">Le sommaire s'écrit ensuite,</strong> sur la page du
          cahier : une ligne par dessin, le titre en français, le sujet et le décor en anglais. Rien
          ne se rend avant que tu lances le rendu.
        </p>
        {autreRefus ? (
          <p className={REFUS} role="alert">
            {messageDEchec(creer.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            aria-disabled={creer.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50 max-md:h-11"
            type="submit"
          >
            Créer le cahier
          </Button>
          <button className={cn(LIEN_BOUTON, "max-md:min-h-11")} onClick={fermer} type="button">
            Annuler
          </button>
        </div>
      </form>
    </section>
  );
}

/** Un champ et son etiquette, son aide et son refus, empiles : la meme forme au telephone. */
function Champ({
  etiquette,
  id,
  aide,
  refus,
  children,
}: {
  etiquette: string;
  /** L'identifiant du champ : l'aide et le refus en derivent (`-aide`, `-refus`). */
  id: string;
  aide: string;
  refus: string | null;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label className={ETIQUETTE} htmlFor={id}>
        {etiquette}
      </label>
      {children}
      <p className={AIDE} id={`${id}-aide`}>
        {aide}
      </p>
      {refus === null ? null : (
        <p className={REFUS} id={`${id}-refus`}>
          {refus}
        </p>
      )}
    </div>
  );
}

/** Un choix parmi quelques valeurs, en boutons radio : plus sur au doigt qu'une liste. */
function Choix<V extends string>({
  etiquette,
  nom,
  options,
  valeur,
  poser,
}: {
  etiquette: string;
  nom: string;
  options: readonly { readonly valeur: V; readonly libelle: string }[];
  valeur: V;
  poser: (valeur: V) => void;
}) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5">
      <legend className={cn(ETIQUETTE, "mb-1.5")}>{etiquette}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <label
            className={cn(
              "inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm max-md:min-h-11",
              option.valeur === valeur
                ? "border-foreground bg-muted font-medium"
                : "border-input bg-background",
            )}
            key={option.valeur}
          >
            <input
              checked={option.valeur === valeur}
              className="size-4 accent-foreground"
              name={nom}
              onChange={() => poser(option.valeur)}
              type="radio"
              value={option.valeur}
            />
            {option.libelle}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
